import type { ReactNode } from 'react';
import { useState } from 'react';
import styled from 'styled-components';

import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '#src/atoms/layout/flex/index.tsx';
import { CloseButton } from '#src/components/dialog/CloseButton/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';

const Root = styled(Vertical).attrs({ gap: 0 })`
  height: 100%;
`;
const Content = styled(Horizontal)`
  isolation: isolate;
  height: 100%;
`;
const Header = styled(Vertical).attrs({ gap: 0 })`
  width: 100%;
  position: absolute;
  top: 0;
  z-index: 1;
`;
const TitleAndCloseButton = styled(SBHorizontal)`
  padding: 0 var(--double-unit);
`;
const StepTitle = styled(Vertical)`
  flex: 1;
  padding-left: calc(5 * var(--unit));
`;

export type Step = {
  onEnd?: () => void;
  bodyElement: ReactNode | ((props: { onNext: () => void }) => ReactNode);
  titleElement?: ReactNode;
};
type Props = {
  onClose: () => void;
  steps: Step[];
  stayOpen?: boolean;
};
export const StepperDialog = ({
  onClose: onCloseProp,
  steps,
  stayOpen,
}: Props) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const currentStep = steps.at(currentStepIndex);

  const onNext = () => {
    steps[currentStepIndex]?.onEnd?.();
    const nextStep = steps.at(currentStepIndex + 1);
    if (!nextStep) {
      if (!stayOpen) {
        onCloseProp();
      }
      return;
    }
    setCurrentStepIndex(index => (index + 1) % steps.length);
  };

  const onClose = () => {
    steps[currentStepIndex]?.onEnd?.();
    onCloseProp();
  };

  if (!steps.length) {
    return null;
  }

  const bodyElement =
    typeof currentStep?.bodyElement === 'function'
      ? currentStep?.bodyElement({ onNext })
      : currentStep?.bodyElement;
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth hideHeader>
      <Root>
        <Header>
          <TitleAndCloseButton>
            <StepTitle center>
              {steps.at(currentStepIndex)?.titleElement}
            </StepTitle>
            <CloseButton transparent onClose={onClose} />
          </TitleAndCloseButton>
        </Header>
        <Content>{bodyElement}</Content>
      </Root>
    </Dialog>
  );
};
